// Column - retro board column with cards and add functionality
// Implements US-2.1: Column display with card stacking
// Implements US-2.2: Card grouping display

import React, { useState } from 'react';
import { ClipboardPlus } from 'lucide-react';
import { Card } from './Card';
import CardGroup from './CardGroup';
import { CardForm } from './CardForm';
import ActionItemForm from './ActionItemForm';
import { Card as CardType, CardGroup as CardGroupType, TemplateColumn } from '../types/card';
import { getIconComponent } from '../utils/iconMapping';
import styles from './Column.module.css';

interface ColumnProps {
  column: TemplateColumn;
  cards: CardType[];
  groups: CardGroupType[];
  sessionId: string;
  isCollecting: boolean;
  isFacilitator: boolean;
  onCreateCard: (columnKey: string, text: string) => void;
  onUpdateCard: (cardId: string, text: string) => void;
  onDeleteCard: (cardId: string) => void;
  onToggleExpand: (cardId: string) => void;
  onToggleDiscussed: (cardId: string) => void;
  // Action Items props (US-2.3)
  onCreateActionItem?: (text: string, owner?: string, dueDate?: string) => void;
  // Grouping props
  selectedCardIds?: string[];
  onToggleSelectCard?: (cardId: string) => void;
  showCardSelect?: boolean;
  onUpdateGroupLabel?: (groupId: string, label: string) => void;
  onUngroupCard?: (cardId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  // Voting props (US-3.2)
  votingActive?: boolean;
  dotsRemaining?: number;
  onVote?: (cardId: string, dots: number) => void;
  // Drag-and-drop props
  onMoveCard?: (cardId: string, targetColumn: string) => void;
}

export const Column: React.FC<ColumnProps> = ({
  column,
  cards,
  groups,
  sessionId,
  isCollecting,
  isFacilitator,
  onCreateCard,
  onUpdateCard,
  onDeleteCard,
  onToggleExpand,
  onToggleDiscussed,
  onCreateActionItem,
  selectedCardIds = [],
  onToggleSelectCard,
  showCardSelect = false,
  onUpdateGroupLabel,
  onUngroupCard,
  onDeleteGroup,
  votingActive = false,
  dotsRemaining = 0,
  onVote,
  onMoveCard,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isAddingAction, setIsAddingAction] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // HANDLE card creation
  const handleCreateCard = (text: string) => {
    onCreateCard(column.key, text);
    setIsAdding(false);
  };

  // HANDLE cancel add
  const handleCancelAdd = () => {
    setIsAdding(false);
  };

  // HANDLE action item creation (US-2.3)
  const handleCreateActionItem = (data: { text: string; owner?: string; dueDate?: string }) => {
    if (onCreateActionItem) {
      onCreateActionItem(data.text, data.owner, data.dueDate);
    }
    setIsAddingAction(false);
  };

  // HANDLE cancel action item add
  const handleCancelActionAdd = () => {
    setIsAddingAction(false);
  };

  // FILTER cards for this column
  const columnCards = cards.filter((card) => card.column === column.key);

  // FILTER groups for this column
  const columnGroups = groups.filter((group) => group.column === column.key);

  // SEPARATE ungrouped cards
  const ungroupedCards = columnCards.filter((card) => !card.groupId);

  // DETERMINE if user can add cards (exclude action items column)
  const canAddCard = isCollecting && column.key !== 'actions';

  // DETERMINE if facilitator can add action items (facilitation phase + actions column)
  const canAddActionItem = !isCollecting && isFacilitator && column.key === 'actions' && onCreateActionItem;

  // HANDLE drag over (allow drop)
  const handleDragOver = (e: React.DragEvent) => {
    if (!isFacilitator || isCollecting) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  // HANDLE drag leave
  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // HANDLE drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (!isFacilitator || isCollecting || !onMoveCard) return;

    const cardId = e.dataTransfer.getData('cardId');
    const sourceColumn = e.dataTransfer.getData('sourceColumn');

    if (!cardId || !sourceColumn) return;

    // PREVENT moving to same column
    if (sourceColumn === column.key) return;

    // MOVE card to this column
    onMoveCard(cardId, column.key);
  };

  return (
    <div
      className={`${styles.column} ${isDragOver ? styles.dragOver : ''}`}
      style={{ borderTopColor: column.borderColor }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={styles.columnHeader}>
        <div className={styles.columnIcon} style={{ backgroundColor: column.color }}>
          {getIconComponent(column.icon, 20)}
        </div>
        <div className={styles.columnInfo}>
          <h3 className={styles.columnTitle}>{column.label}</h3>
          {column.description && (
            <p className={styles.columnDescription}>{column.description}</p>
          )}
        </div>
        <div className={styles.columnCount}>{columnCards.length}</div>
      </div>

      <div className={styles.columnContent}>
        {/* SHOW add card button if allowed */}
        {canAddCard && !isAdding && (
          <button
            className={styles.btnAddCard}
            onClick={() => setIsAdding(true)}
          >
            + Karte hinzufügen
          </button>
        )}

        {/* SHOW add action item button if allowed (US-2.3) */}
        {canAddActionItem && !isAddingAction && (
          <button
            className={styles.btnAddActionItem}
            onClick={() => setIsAddingAction(true)}
          >
            <ClipboardPlus size={16} /> Action Item hinzufügen
          </button>
        )}

        {/* SHOW card form if adding */}
        {isAdding && (
          <div className={styles.addCardForm}>
            <CardForm
              onSubmit={handleCreateCard}
              onCancel={handleCancelAdd}
              submitLabel="Karte hinzufügen"
            />
          </div>
        )}

        {/* SHOW action item form if adding (US-2.3) */}
        {isAddingAction && (
          <ActionItemForm
            onSubmit={handleCreateActionItem}
            onCancel={handleCancelActionAdd}
            mode="create"
          />
        )}

        {/* RENDER groups and cards */}
        <div className={styles.cardsList}>
          {/* RENDER card groups */}
          {columnGroups.map((group) => {
            const groupCards = columnCards.filter((card) => card.groupId === group.id);
            return (
              <CardGroup
                key={group.id}
                group={group}
                cards={groupCards}
                isFacilitator={isFacilitator}
                isCollectionPhase={isCollecting}
                sessionId={sessionId}
                onUpdateCard={onUpdateCard}
                onDeleteCard={onDeleteCard}
                onToggleExpand={onToggleExpand}
                onToggleDiscussed={onToggleDiscussed}
                onUpdateGroupLabel={onUpdateGroupLabel || (() => {})}
                onUngroupCard={onUngroupCard || (() => {})}
                onDeleteGroup={onDeleteGroup || (() => {})}
              />
            );
          })}

          {/* RENDER ungrouped cards */}
          {ungroupedCards.map((card) => (
            <Card
              key={card.id}
              card={card}
              isOwner={card.authorSessionId === sessionId}
              isCollecting={isCollecting}
              isFacilitator={isFacilitator}
              onUpdate={onUpdateCard}
              onDelete={onDeleteCard}
              onToggleExpand={onToggleExpand}
              onToggleDiscussed={onToggleDiscussed}
              isSelected={selectedCardIds.includes(card.id)}
              onToggleSelect={onToggleSelectCard}
              showSelect={showCardSelect}
              votingActive={votingActive}
              dotsRemaining={dotsRemaining}
              onVote={onVote}
            />
          ))}
        </div>

        {/* SHOW empty state if no cards */}
        {columnCards.length === 0 && !isAdding && (
          <div className={styles.emptyState}>
            <p>Noch keine Karten</p>
          </div>
        )}
      </div>
    </div>
  );
};
